import { MULTIPLAYER_CONFIG } from "../config/multiplayer.ts";
import {
  INPUT_INTENT_SCHEMA_VERSION,
  type InputIntent,
} from "../input/intents.ts";
import {
  validateCharacterAppearance,
  type CharacterAppearance,
} from "../sim/appearance.ts";
import type {
  MultiplayerRoomDelta,
  MultiplayerRoomSnapshot,
} from "../sim/session/types.ts";

export const MULTIPLAYER_PROTOCOL_VERSION =
  MULTIPLAYER_CONFIG.protocolVersion;

export interface RoomDirectoryEntry {
  readonly id: string;
  readonly name: string;
  readonly playerCount: number;
  readonly capacity: number;
  readonly passwordProtected: boolean;
  readonly createdAt: string;
}

export interface CreateRoomRequest {
  readonly roomName: string;
  readonly displayName: string;
  readonly password?: string;
  readonly appearance: CharacterAppearance;
}

export interface JoinRoomRequest {
  readonly displayName: string;
  readonly password?: string;
  readonly appearance: CharacterAppearance;
  readonly resumeToken?: string;
}

export interface RoomJoinGrant {
  readonly room: RoomDirectoryEntry;
  readonly ticket: string;
  readonly socketPath: string;
}

export interface ChatMessage {
  readonly id: string;
  readonly playerId: string;
  readonly displayName: string;
  readonly text: string;
  readonly sentAt: string;
}

export interface RoomServerDiagnostics {
  readonly tickSampleCount: number;
  readonly tickDurationP99Milliseconds: number;
  readonly tickDurationMaximumMilliseconds: number;
}

export type RoomCommand =
  | Readonly<{
      kind: "neighborhood-action";
      action:
        | "accept_ona_parcel"
        | "inspect_parcel"
        | "deliver_ruta_sealed"
        | "deliver_ruta_opened"
        | "entrust_mantas";
    }>
  | Readonly<{
      kind: "set-environment";
      season?: "spring" | "summer" | "autumn" | "winter";
      timeOfDayHours?: number;
      frozen?: boolean;
      surfaceWetness01?: number;
    }>
  | Readonly<{
      kind: "set-appearance";
      appearance: CharacterAppearance;
    }>
  | Readonly<{
      kind: "transfer-player";
      position: Readonly<{ x: number; y: number; z: number }>;
      facingYaw: number;
    }>
  | Readonly<{
      kind: "set-neighborhood-preset";
      preset: "locked" | "blue-gate" | "rooftop-shortcut";
    }>;

export type ClientRoomMessage =
  | Readonly<{
      type: "hello";
      protocolVersion: number;
      contentBuildId: string;
      ticket: string;
    }>
  | Readonly<{
      type: "input";
      intents: readonly InputIntent[];
    }>
  | Readonly<{
      type: "chat";
      text: string;
    }>
  | Readonly<{
      type: "command";
      requestId: string;
      command: RoomCommand;
    }>
  | Readonly<{
      type: "snapshot-ack";
      sequence: number;
    }>;

export type ServerRoomMessage =
  | Readonly<{
      type: "welcome";
      protocolVersion: number;
      contentBuildId: string;
      playerId: string;
      resumeToken: string;
      sequence: number;
      snapshot: MultiplayerRoomSnapshot;
      chatHistory: readonly ChatMessage[];
    }>
  | Readonly<{
      type: "snapshot";
      sequence: number;
      kind: "keyframe";
      lastProcessedInputTick: number;
      snapshot: MultiplayerRoomSnapshot;
      diagnostics?: RoomServerDiagnostics;
    }>
  | Readonly<{
      type: "snapshot";
      sequence: number;
      baseSequence: number;
      kind: "delta";
      lastProcessedInputTick: number;
      delta: MultiplayerRoomDelta;
      diagnostics?: RoomServerDiagnostics;
    }>
  | Readonly<{ type: "chat"; message: ChatMessage }>
  | Readonly<{
      type: "presence";
      playerId: string;
      displayName: string;
      connected: boolean;
    }>
  | Readonly<{
      type: "command-result";
      requestId: string;
      accepted: boolean;
      reason?: string;
    }>
  | Readonly<{
      type: "error";
      code: string;
      message: string;
      recoverable: boolean;
    }>;

type JsonObject = Record<string, unknown>;

function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function cleanText(value: unknown, maximum: number): string | null {
  if (typeof value !== "string") return null;
  const cleaned = value.replace(/[\u0000-\u001f\u007f]/gu, " ").trim();
  const characters = [...cleaned];
  if (characters.length === 0 || characters.length > maximum) return null;
  return characters.join("");
}

export function normalizeRoomName(value: unknown): string | null {
  return cleanText(value, MULTIPLAYER_CONFIG.roomNameMaximumCharacters);
}

export function normalizeDisplayName(value: unknown): string | null {
  return cleanText(value, MULTIPLAYER_CONFIG.displayNameMaximumCharacters);
}

export function normalizePassword(value: unknown): string | null {
  if (value === undefined || value === "") return "";
  if (typeof value !== "string") return null;
  const length = [...value].length;
  return length > 0 && length <= MULTIPLAYER_CONFIG.passwordMaximumCharacters
    ? value
    : null;
}

export function normalizeChatText(value: unknown): string | null {
  return cleanText(value, MULTIPLAYER_CONFIG.chatMaximumCharacters);
}

const INPUT_BOOLEAN_FIELDS = [
  "jumpPressed",
  "ascendHeld",
  "descendHeld",
  "sprintHeld",
  "interactPressed",
  "cycleSeatPressed",
  "toggleFlightPressed",
  "brakeHeld",
  "reverseHeld",
  "handbrakeHeld",
  "hornHeld",
  "lookBehindHeld",
  "toggleHeadlightsPressed",
  "toggleBeaconPressed",
  "toggleSirenPressed",
  "toggleLeftIndicatorPressed",
  "toggleRightIndicatorPressed",
  "recoverVehiclePressed",
] as const satisfies readonly (keyof InputIntent)[];

function parseInputIntent(value: unknown): InputIntent | null {
  if (
    !isObject(value) ||
    value.schemaVersion !== INPUT_INTENT_SCHEMA_VERSION ||
    typeof value.playerId !== "string" ||
    value.playerId.length === 0 ||
    !Number.isSafeInteger(value.tick) ||
    !Number.isFinite(value.moveX) ||
    !Number.isFinite(value.moveZ) ||
    !Number.isFinite(value.lookYaw) ||
    Math.abs(Number(value.moveX)) > 1 ||
    Math.abs(Number(value.moveZ)) > 1 ||
    INPUT_BOOLEAN_FIELDS.some((field) => typeof value[field] !== "boolean")
  ) {
    return null;
  }
  return value as unknown as InputIntent;
}

const NEIGHBORHOOD_ACTIONS = new Set([
  "accept_ona_parcel",
  "inspect_parcel",
  "deliver_ruta_sealed",
  "deliver_ruta_opened",
  "entrust_mantas",
]);
const SEASONS = new Set(["spring", "summer", "autumn", "winter"]);

function parseRoomCommand(value: unknown): RoomCommand | null {
  if (!isObject(value)) return null;
  if (
    value.kind === "neighborhood-action" &&
    typeof value.action === "string" &&
    NEIGHBORHOOD_ACTIONS.has(value.action)
  ) {
    return value as unknown as RoomCommand;
  }
  if (value.kind === "set-appearance") {
    try {
      validateCharacterAppearance(value.appearance);
      return {
        kind: "set-appearance",
        appearance: structuredClone(value.appearance),
      };
    } catch {
      return null;
    }
  }
  if (
    value.kind === "transfer-player" &&
    isObject(value.position) &&
    Number.isFinite(value.position.x) &&
    Number.isFinite(value.position.y) &&
    Number.isFinite(value.position.z) &&
    Number.isFinite(value.facingYaw)
  ) {
    return {
      kind: "transfer-player",
      position: {
        x: Number(value.position.x),
        y: Number(value.position.y),
        z: Number(value.position.z),
      },
      facingYaw: Number(value.facingYaw),
    };
  }
  if (
    value.kind === "set-neighborhood-preset" &&
    (
      value.preset === "locked" ||
      value.preset === "blue-gate" ||
      value.preset === "rooftop-shortcut"
    )
  ) {
    return { kind: "set-neighborhood-preset", preset: value.preset };
  }
  if (value.kind !== "set-environment") return null;
  if (
    (value.season !== undefined &&
      (typeof value.season !== "string" || !SEASONS.has(value.season))) ||
    (value.timeOfDayHours !== undefined && typeof value.timeOfDayHours !== "number") ||
    (value.frozen !== undefined && typeof value.frozen !== "boolean") ||
    (value.surfaceWetness01 !== undefined && typeof value.surfaceWetness01 !== "number")
  ) {
    return null;
  }
  return value as unknown as RoomCommand;
}

export function parseClientRoomMessage(value: unknown): ClientRoomMessage | null {
  if (!isObject(value) || typeof value.type !== "string") return null;
  if (
    value.type === "hello" &&
    typeof value.protocolVersion === "number" &&
    typeof value.contentBuildId === "string" &&
    typeof value.ticket === "string"
  ) {
    return {
      type: "hello",
      protocolVersion: value.protocolVersion,
      contentBuildId: value.contentBuildId,
      ticket: value.ticket,
    };
  }
  if (value.type === "input" && Array.isArray(value.intents)) {
    const intents = value.intents
      .slice(0, MULTIPLAYER_CONFIG.inputBatchMaximum)
      .map(parseInputIntent);
    if (intents.some((intent) => intent === null)) return null;
    return {
      type: "input",
      intents: intents as InputIntent[],
    };
  }
  if (value.type === "chat") {
    const text = normalizeChatText(value.text);
    return text === null ? null : { type: "chat", text };
  }
  if (
    value.type === "snapshot-ack" &&
    Number.isSafeInteger(value.sequence) &&
    Number(value.sequence) >= 0
  ) {
    return { type: "snapshot-ack", sequence: Number(value.sequence) };
  }
  if (
    value.type === "command" &&
    typeof value.requestId === "string" &&
    value.requestId.length > 0 &&
    value.requestId.length <= 100
  ) {
    const command = parseRoomCommand(value.command);
    return command === null
      ? null
      : { type: "command", requestId: value.requestId, command };
  }
  return null;
}

export function parseJsonMessage(data: string | ArrayBuffer): unknown {
  const text = typeof data === "string"
    ? data
    : new TextDecoder().decode(data);
  if (new TextEncoder().encode(text).byteLength > MULTIPLAYER_CONFIG.websocketFrameMaximumBytes) {
    throw new Error("WebSocket frame is too large");
  }
  return JSON.parse(text) as unknown;
}
